import type { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Sheet, Text } from "@amzn/storm-ui";
import * as React from "react";
import { useStore } from "zustand";

import type { Email } from "../../api/g4sApi/types/operations";
import type { Summary } from "../../api/g4sApi/types/operations/createDeckDecoration";
import { G4sPlacement } from "../../constants/g4sPlacement";
import { workbenchStore } from "../../store/workbench";
import type { DownloadAsset } from "../../types/common";
import { PdfSelectionViewer } from "../common/PdfSelectionViewer";
import {
    FooterComponentBuilder,
    FooterDownloadButton,
} from "../common/insightDetail.components";
import {
    DivJustifyItemCenter,
    ToolbarButtonGroup,
} from "../common/styled.components";
import { DeckDecorationWithLanguageModalButton } from "../createDeckDecoration/DeckDecorationWithLanguageModalButton";
import { WorkbenchActions } from "../discovery/insights/insightDetails/WorkbenchActions";
import {
    StyledDetailSheetPDFViewerContainer,
    StyledSheetFooter,
} from "../discovery/insights/insightDetails/styles";

import {
    RelevantInsightBanner,
    RelevantInsightTitle,
    StyledRelevantInsightSheetBody,
    StyledRelevantInsightSheetHeader,
} from "./relevantInsight.components";

export interface RelevantInsightSheetProps {
    previewAsset?: DownloadAsset;
    downloadAsset?: DownloadAsset;
    insightType: InsightType;
    identifier: string;
    isSheetOpen: boolean;
    setSheetOpen: (e: boolean) => void;
}

export type DeckDecorationContentType = Email | Summary;

const noDataMessage: JSX.Element = (
    <DivJustifyItemCenter>
        <Text>There are currently no suggested insights available.</Text>
    </DivJustifyItemCenter>
);

export const RelevantInsightSheet: React.FunctionComponent<
    RelevantInsightSheetProps
> = (props) => {
    const {
        previewAsset,
        downloadAsset,
        insightType,
        identifier,
        isSheetOpen,
        setSheetOpen,
    } = props;

    const [numPages, setNumPages] = React.useState<number>(0);

    const [selectedSlides, setSelectedSlides] = React.useState(
        new Set<number>(),
    );

    const [getSlideIndexes] = useStore(workbenchStore, (state) => [
        state.getSlideIndexes,
    ]);

    React.useEffect(() => {
        if (isSheetOpen) setSelectedSlides(getSlideIndexes(identifier));
    }, [isSheetOpen]);

    const handleOnClose = () => {
        setSheetOpen(false);
        setNumPages(0);
        setSelectedSlides(new Set<number>());
    };

    return (
        <>
            <Sheet
                isOpen={isSheetOpen}
                onClose={handleOnClose}
                sheetElementId="ihub-portal"
                withCloseButton
                side="end"
                closeButton={{
                    type: "icon",
                    label: "",
                }}
                padding="none"
                data-testid={`relevant-insight-sheet-${identifier}`}
            >
                <StyledRelevantInsightSheetBody>
                    <StyledRelevantInsightSheetHeader>
                        <RelevantInsightTitle numPages={numPages} />
                        <RelevantInsightBanner />
                    </StyledRelevantInsightSheetHeader>
                    <StyledDetailSheetPDFViewerContainer>
                        <PdfSelectionViewer
                            numPages={numPages}
                            setNumPages={setNumPages}
                            url={previewAsset ? previewAsset.downloadUrl! : ""}
                            insightType={insightType}
                            identifier={identifier}
                            selectedSlides={selectedSlides}
                            setSelectedSlides={setSelectedSlides}
                            isCompliant={true}
                            noData={noDataMessage}
                        />
                    </StyledDetailSheetPDFViewerContainer>
                    <StyledSheetFooter>
                        <FooterComponentBuilder
                            loading={false}
                            handleClose={handleOnClose}
                            identifier={identifier}
                            insightType={insightType}
                            footerComponents={[
                                <DeckDecorationWithLanguageModalButton
                                    documentUrl={
                                        previewAsset
                                            ? previewAsset.downloadUrl
                                            : undefined
                                    }
                                    identifier={identifier}
                                    insightType={insightType}
                                    placement={G4sPlacement.SIDE_PANEL}
                                />,
                                <ToolbarButtonGroup>
                                    <FooterDownloadButton
                                        handleDownload={() => {}}
                                        downloadAsset={downloadAsset}
                                        identifier={identifier}
                                        insightType={insightType}
                                        showDropdown={false}
                                    />
                                </ToolbarButtonGroup>,
                                <WorkbenchActions
                                    identifier={identifier}
                                    selectedSlides={selectedSlides}
                                    setSelectedSlides={setSelectedSlides}
                                    outputFileFormats={[]}
                                    numPages={numPages}
                                    downloadAsset={downloadAsset}
                                    previewAsset={previewAsset}
                                    insightType={insightType}
                                    isCompliant={true}
                                />,
                            ]}
                        />
                    </StyledSheetFooter>
                </StyledRelevantInsightSheetBody>
            </Sheet>
        </>
    );
};
